import React, { useEffect, useState, useContext } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import cn from 'classnames'
import { CustomerContext } from '../../../contexts/CustomerContext'
import Option from '../../../models/Option'
import AttentionBanner from '../../attention-banner'
import AppointmentOption from '../../appointment-option'
import Spinner from '../../Spinner'
import { routes } from '../../../routes'
import classes from './appointment-session-selection.module.sass'
import useQueryString from '../../../hooks/use-query-string'
import { Price } from '../../../models'
import placeholder from '../../../assets/front_page_form_img.png'
import Image from '../../Image'
import { getOptionName } from '../../../utils/functions'

// ----- Senior-only helpers ---------------------------------------------------
// Maps a feature label (e.g. "Cap & Gown", "30 Poses") to a small SVG. Matches
// the icon set used on the /seniors landing page so the booking flow and the
// landing page share a consistent visual language.
const CapIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
  </svg>
)
const BookIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 4H3c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h6c.55 0 1.05-.22 1.41-.59L12 19l1.59 1.41c.36.37.86.59 1.41.59h6c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM3 19V6h8v13.08L9.92 18H3zm18 0h-6.92L13 19.08V6h8v13z"/>
  </svg>
)
const CameraIcon = () => (
  <svg className="seniors-package-card__feature-icon" viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" aria-hidden="true">
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
    <circle cx="12" cy="13" r="2.6"/>
  </svg>
)

const featureIconFor = (label) => {
  if (/cap.*?gown/i.test(label)) return <CapIcon />
  if (/yearbook.*?pose/i.test(label)) return <BookIcon />
  if (/poses?\b/i.test(label)) return <CameraIcon />
  return null
}

// Build the feature list for a session card. Mirrors the senior landing logic:
// for the top three tiers (Super / Deluxe / Standard), lead with the pose
// count; for the lower-tier extras (Basic / Economy), keep the original order.
const buildFeatures = (option, isExtra) => {
  const raw = (option.description || '').split(',').map((s) => s.trim()).filter(Boolean)
  if (raw.length === 0) {
    // Fallback to the existing data model if description is empty.
    const fallback = []
    if (option.poses > 0) fallback.push(`${option.poses} Poses`)
    ;(option.imageTypes || []).forEach((it) => fallback.push(it.name))
    return fallback
  }
  if (isExtra) return raw
  const posesIdx = raw.findIndex((item) => /^\d+\s+Poses?$/i.test(item))
  if (posesIdx > 0) return [raw[posesIdx], ...raw.slice(0, posesIdx), ...raw.slice(posesIdx + 1)]
  return raw
}

const SeniorSessionCard = ({ option, i, onSelect, compact = false }) => {
  const [first, second] = getOptionName(option.name)
  const sessionName = [first, second].filter(Boolean).join(' ')
  const isFeatured = (first || '').toLowerCase() === 'deluxe'
  const isShorter = i === 2  // Standard — slightly shorter than Super for a 3-tier silhouette
  if (!option.prices || option.prices.length === 0) return null

  const priceVal = Number(option.prices[0].price) || 0
  const dollars = Math.floor(priceVal)
  const cents = String(Math.round((priceVal - dollars) * 100)).padStart(2, '0')
  const features = buildFeatures(option, compact)

  const renderPrice = () => (
    <p className={cn('seniors-package-card__price', classes.priceBelow)}>
      <span className="seniors-package-card__currency">$</span>{dollars}
      <span className="seniors-package-card__cents">.{cents}</span>
    </p>
  )

  const renderFeatures = () => (
    features.length > 0 && (
      <ul className={cn('seniors-package-card__features', classes.featuresLeft)}>
        {features.map((feat) => {
          const icon = featureIconFor(feat)
          return (
            <li key={feat} className={icon ? 'has-feature-icon' : ''}>
              {icon}
              {feat}
            </li>
          )
        })}
      </ul>
    )
  )

  // Compact variant — Basic / Economy on the secondary row. No image, smaller
  // padding, designed to be deliberately less enticing than the main tier.
  if (compact) {
    return (
      <div className={cn('seniors-package-card', classes.compactCard)}>
        <h3 className="seniors-package-card__name">{sessionName}</h3>
        {renderFeatures()}
        {renderPrice()}
        <button
          type="button"
          className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
          onClick={() => onSelect(option)}
        >
          Select
        </button>
      </div>
    )
  }

  return (
    <div className={cn('seniors-package-card', classes.seniorCard, {
      'seniors-package-card--featured': isFeatured,
      [classes.seniorCardShorter]: isShorter,
    })}>
      <div className={classes.seniorCardImage}>
        <Image
          alt={sessionName}
          src={option.imageUrl && !option.imageUrl.includes('missing.png') ? option.imageUrl : placeholder}
        />
        {isFeatured && (
          <div className={cn('seniors-package-card__badge', classes.badgeOverImage)}>
            Most Popular
          </div>
        )}
      </div>
      <h3 className="seniors-package-card__name">{sessionName}</h3>
      {renderFeatures()}
      {renderPrice()}
      <button
        type="button"
        className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
        onClick={() => onSelect(option)}
      >
        Select
      </button>
    </div>
  )
}

// Compact card for the "Upgrade ..." options. No image, no feature list —
// just name, price, and a Select button. Lives in its own section below the
// session grid.
const SeniorUpgradeCard = ({ option, onSelect }) => {
  if (!option.prices || option.prices.length === 0) return null
  const priceVal = Number(option.prices[0].price) || 0
  const dollars = Math.floor(priceVal)
  const cents = String(Math.round((priceVal - dollars) * 100)).padStart(2, '0')
  // Strip a leading "X-" / "X- " prefix from upgrade names so they read cleanly.
  const cleanName = (option.name || '').replace(/^[A-Z]-\s*/, '')
  // Prefer the description field; fall back to tagline so admins can fill in
  // either column in the back office.
  const description = ((option.description || '').trim() || (option.tagline || '').trim())

  return (
    <div className={cn('seniors-package-card', classes.upgradeCard)}>
      <h3 className="seniors-package-card__name">{cleanName}</h3>
      {description && (
        <p className={classes.upgradeDescription}>{description}</p>
      )}
      <p className={cn('seniors-package-card__price', classes.priceBelow)}>
        <span className="seniors-package-card__currency">$</span>{dollars}
        <span className="seniors-package-card__cents">.{cents}</span>
      </p>
      <button
        type="button"
        className={cn('seniors-btn', 'seniors-btn--outline', classes.selectBtn)}
        onClick={() => onSelect(option)}
      >
        Select
      </button>
    </div>
  )
}

// 4-step indicator. Connector lines live between items as their own elements
// so they always start AFTER the previous label and end BEFORE the next icon,
// regardless of label width.
const SeniorSteps = () => (
  <ol className={classes.steps} aria-label="Booking progress">
    <li className={cn(classes.stepItem, classes.stepItemDone)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Find your student</span>
    </li>
    <span className={cn(classes.stepConnector, classes.stepConnectorDone)} aria-hidden="true" />
    <li className={cn(classes.stepItem, classes.stepItemActive)}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9"/>
          <circle cx="12" cy="12" r="3" fill="currentColor"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose your session</span>
    </li>
    <span className={classes.stepConnector} aria-hidden="true" />
    <li className={classes.stepItem}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
          <line x1="8" y1="3" x2="8" y2="7"/>
          <line x1="16" y1="3" x2="16" y2="7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Choose date/time</span>
    </li>
    <span className={classes.stepConnector} aria-hidden="true" />
    <li className={classes.stepItem}>
      <span className={classes.stepNum}>
        <svg className={classes.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={classes.stepLabel}>Confirm</span>
    </li>
  </ol>
)
// ----- end senior helpers ----------------------------------------------------

const AppointmentOptionSelection = () => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const { packageId } = useParams()
  const { o: isOther, senior } = useQueryString()
  const history = useHistory()
  const [options, setOptions] = useState([])

  useEffect(() => {
    const priceScope = Price.where({ hasPrice: true });

    Option
      .includes(['image_types', 'prices'])
      .order('abbreviation')
      .merge({ prices: priceScope })
      .where({ packageId, hasPurchasedPreviousOption: [(customer.email || '').toLowerCase(), packageId] })
      .all()
      .then((res) => {
        setOptions(res.data)
      })
  }, [packageId])

  const onSelect = (opt) => {
    const { currentOrderPackage } = customer

    currentOrderPackage.optionId = opt.id
    currentOrderPackage.apiOption = opt
    currentOrderPackage.option = opt

    setCustomer({ ...customer, currentOrderPackage })
    history.push(routes.appointmentSelection(senior ? { senior: 'true' } : {}))
  }

  if (options.length === 0) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  // Senior flow — restyled to match the /seniors landing page.
  if (senior) {
    const isUpgrade = (opt) => /upgrade/i.test(opt.name || '')
    const sessions = options.filter((opt) => !isUpgrade(opt))
    const mainSessions = sessions.slice(0, 3)    // Super / Deluxe / Standard
    const extraSessions = sessions.slice(3)      // Basic / Economy (and any beyond)

    return (
      <div className={classes.seniorWrap}>
        <SeniorSteps />
        <div className={classes.seniorGrid}>
          {mainSessions.map((option, i) => (
            <SeniorSessionCard key={option.id} option={option} i={i} onSelect={onSelect} />
          ))}
        </div>

        {extraSessions.length > 0 && (
          <div className={classes.seniorGridExtras}>
            {extraSessions.map((option, idx) => (
              <SeniorSessionCard
                key={option.id}
                option={option}
                i={3 + idx}
                onSelect={onSelect}
                compact
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Default (elementary / retake) flow — unchanged.
  return (
    <>
      <div>
        <AttentionBanner content="Step 1. Choose Your Session" />
        <div className={classes.content}>
          <div className={classes.optionsContainer}>
            {options.map((option, i) => (
              <AppointmentOption
                key={option.id}
                option={option}
                handleSelect={onSelect}
                i={i}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default AppointmentOptionSelection
